import React, { useState, useEffect, useRef } from 'react';
import { db, auth } from '../firebase';
import { collection, onSnapshot, query, orderBy } from 'firebase/firestore';

const GENERATE_URL = 'https://us-central1-core6-marketing.cloudfunctions.net/generateSocialContent';

const POST_TYPES = [
  { key: 'blog',        label: 'Blog',        inputLabel: 'Blog URL',          placeholder: 'https://…/the-blog-post', multiline: false, required: true,  hint: 'The article the post points to — Claude reads it for the caption.' },
  { key: 'service',     label: 'Service',     inputLabel: 'Service or page',   placeholder: 'e.g. Invisalign — or a service page URL', multiline: false, required: false },
  { key: 'tip',         label: 'Quick Tip',   inputLabel: 'Tip topic',         placeholder: 'e.g. caring for your retainer', multiline: false, required: false },
  { key: 'testimonial', label: 'Testimonial', inputLabel: 'Paste the review',  placeholder: 'Paste the customer review text here…', multiline: true,  required: true },
  { key: 'general',     label: 'General',     inputLabel: 'Notes',             placeholder: 'Anything Claude should know for this post…', multiline: true, required: false },
];

const MAX_IMAGE_BYTES = 8 * 1024 * 1024;

export default function SocialContent() {
  const [clients, setClients] = useState([]);
  const [selectedClientId, setSelectedClientId] = useState('');
  const [postType, setPostType] = useState('blog');
  const [input, setInput] = useState('');
  const [image, setImage] = useState(null); // { dataUrl, data, mediaType, name }
  const [generating, setGenerating] = useState(false);
  const [error, setError] = useState('');
  const [caption, setCaption] = useState('');
  const [copied, setCopied] = useState(false);
  const fileRef = useRef(null);

  useEffect(() => {
    const unsub = onSnapshot(
      query(collection(db, 'clients'), orderBy('businessName')),
      snap => setClients(snap.docs.map(d => ({ id: d.id, ...d.data() })).filter(c => !c.isArchived)),
      err => console.error('clients snapshot error:', err)
    );
    return unsub;
  }, []);

  const client = clients.find(c => c.id === selectedClientId) || null;
  const typeCfg = POST_TYPES.find(t => t.key === postType) || POST_TYPES[0];

  useEffect(() => { setCaption(''); setError(''); setCopied(false); }, [selectedClientId]);
  useEffect(() => { setInput(''); setCaption(''); setError(''); }, [postType]);

  const onPickImage = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith('image/')) { setError('Please choose an image file.'); return; }
    if (file.size > MAX_IMAGE_BYTES) { setError('Image is too large — please use one under 8 MB.'); return; }
    setError('');
    const reader = new FileReader();
    reader.onload = () => {
      const dataUrl = reader.result;
      const data = String(dataUrl).split(',')[1] || '';
      setImage({ dataUrl, data, mediaType: file.type, name: file.name });
    };
    reader.readAsDataURL(file);
  };

  const clearImage = () => {
    setImage(null);
    if (fileRef.current) fileRef.current.value = '';
  };

  const inputRequiredMissing = typeCfg.required && !input.trim();
  const canGenerate = !!client && !inputRequiredMissing && !generating;

  const handleGenerate = async () => {
    setError('');
    setCaption('');
    setCopied(false);
    setGenerating(true);
    try {
      if (!auth.currentUser) throw new Error('You are signed out — sign in again to generate.');
      const idToken = await auth.currentUser.getIdToken();
      const res = await fetch(GENERATE_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${idToken}` },
        body: JSON.stringify({
          clientId: selectedClientId,
          postType,
          input: input.trim(),
          image: image ? { data: image.data, mediaType: image.mediaType } : null,
        }),
      });
      const data = await res.json();
      if (!res.ok || !data.success) throw new Error(data.error || data.details || 'Generation failed');
      setCaption(data.caption);
    } catch (err) {
      setError(err.message);
    } finally {
      setGenerating(false);
    }
  };

  const copyCaption = async () => {
    try {
      await navigator.clipboard.writeText(caption || '');
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      alert('Copy failed — select the text and copy manually.');
    }
  };


  return (
    <div className="page" style={{ maxWidth: 1000 }}>
      <div style={{ marginBottom: 20 }}>
        <h1 style={{ fontSize: 22, fontWeight: 700, color: 'var(--text-primary)', marginBottom: 4 }}>Social Content</h1>
        <p style={{ fontSize: 13, color: 'var(--text-secondary)' }}>
          Upload your finished image — Claude reviews it and writes the caption from the client's full profile data
        </p>
      </div>

      {/* Client selector */}
      <div className="card" style={{ padding: '16px 20px', marginBottom: 20, display: 'flex', alignItems: 'center', gap: 16, flexWrap: 'wrap' }}>
        <label style={{ fontSize: 12, fontWeight: 600, color: 'var(--text-secondary)', textTransform: 'uppercase', letterSpacing: '.4px', whiteSpace: 'nowrap' }}>
          Select Client
        </label>
        <select
          value={selectedClientId}
          onChange={e => setSelectedClientId(e.target.value)}
          style={{ minWidth: 280, background: 'var(--bg-primary)', border: '1px solid var(--border)', borderRadius: 7, padding: '8px 12px', fontSize: 14, color: 'var(--text-primary)', outline: 'none' }}
        >
          <option value="">— Choose a client —</option>
          {clients.map(c => <option key={c.id} value={c.id}>{c.businessName}</option>)}
        </select>
      </div>

      {!client ? (
        <div className="card" style={{ textAlign: 'center', padding: '64px 20px', color: 'var(--text-muted)' }}>
          <div style={{ fontSize: 32, marginBottom: 12 }}>🖼️</div>
          <div style={{ fontSize: 15, fontWeight: 600, marginBottom: 6, color: 'var(--text-secondary)' }}>Select a client to get started</div>
          <div style={{ fontSize: 13 }}>Then upload an image and pick a post type to generate a caption</div>
        </div>
      ) : (
        <>
          <div style={{ fontSize: 12, color: 'var(--text-muted)', marginBottom: 16, lineHeight: 1.5 }}>
            Captions — including hashtags — are written automatically from this client's brand voice and uploaded profile data.
          </div>

          <div className="card" style={{ padding: 22, marginBottom: 20, border: '1px solid #4f46e544' }}>
            <div style={{ fontSize: 13, fontWeight: 700, color: '#818cf8', textTransform: 'uppercase', letterSpacing: '.5px', marginBottom: 16 }}>✦ Create Caption</div>

            {error && <div className="error-box" style={{ marginBottom: 14, fontSize: 12 }}>{error}</div>}

            {/* Post type */}
            <div className="form-field">
              <label>Post Type</label>
              <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginTop: 4 }}>
                {POST_TYPES.map(t => (
                  <button
                    key={t.key}
                    type="button"
                    onClick={() => setPostType(t.key)}
                    style={{ padding: '7px 14px', borderRadius: 7, border: '1px solid', cursor: 'pointer', fontSize: 13, fontWeight: 600, background: postType === t.key ? '#0a1f12' : 'var(--bg-primary)', borderColor: postType === t.key ? '#16a34a55' : 'var(--border)', color: postType === t.key ? '#4ade80' : 'var(--text-secondary)' }}
                  >
                    {t.label}
                  </button>
                ))}
              </div>
            </div>

            {/* Adaptive input */}
            <div className="form-field">
              <label>
                {typeCfg.inputLabel}
                {!typeCfg.required && <span style={{ color: 'var(--text-muted)', fontWeight: 400, textTransform: 'none', letterSpacing: 0 }}> — optional</span>}
              </label>
              {typeCfg.multiline ? (
                <textarea
                  value={input}
                  onChange={e => { setInput(e.target.value); setError(''); }}
                  placeholder={typeCfg.placeholder}
                  disabled={generating}
                  style={{ minHeight: 90, background: 'var(--bg-primary)', border: '1px solid var(--border)', borderRadius: 7, padding: '10px 12px', fontSize: 13, color: 'var(--text-primary)', outline: 'none', lineHeight: 1.5 }}
                />
              ) : (
                <input
                  type="text"
                  value={input}
                  onChange={e => { setInput(e.target.value); setError(''); }}
                  placeholder={typeCfg.placeholder}
                  disabled={generating}
                  style={{ background: 'var(--bg-primary)', border: '1px solid var(--border)', borderRadius: 7, padding: '9px 12px', fontSize: 13, color: 'var(--text-primary)', outline: 'none' }}
                />
              )}
              {typeCfg.hint && <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 4 }}>{typeCfg.hint}</div>}
            </div>

            {/* Image upload */}
            <div className="form-field">
              <label>Image <span style={{ color: 'var(--text-muted)', fontWeight: 400, textTransform: 'none', letterSpacing: 0 }}>— optional; upload the finished image for Claude to review</span></label>
              {image ? (
                <div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
                  <img src={image.dataUrl} alt="upload preview" style={{ width: 96, height: 96, objectFit: 'cover', borderRadius: 8, border: '1px solid var(--border)' }} />
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontSize: 13, color: 'var(--text-primary)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{image.name}</div>
                    <button type="button" className="btn btn-sm btn-secondary" style={{ marginTop: 8 }} onClick={clearImage} disabled={generating}>Remove</button>
                  </div>
                </div>
              ) : (
                <div
                  onClick={() => fileRef.current?.click()}
                  style={{ border: '1px dashed var(--border)', borderRadius: 8, padding: '24px 16px', textAlign: 'center', cursor: 'pointer', color: 'var(--text-muted)', fontSize: 13, background: 'var(--bg-primary)' }}
                >
                  📎 Click to upload an image (PNG, JPG, WEBP — under 8 MB)
                </div>
              )}
              <input ref={fileRef} type="file" accept="image/*" onChange={onPickImage} style={{ display: 'none' }} />
            </div>

            <button
              className="btn btn-primary"
              style={{ width: '100%', background: '#4f46e5', borderColor: '#4f46e5' }}
              onClick={handleGenerate}
              disabled={!canGenerate}
            >
              {generating ? (
                <span style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8 }}>
                  <span className="spinner" style={{ width: 14, height: 14 }}></span> Reviewing image & writing caption…
                </span>
              ) : '✦ Generate Caption'}
            </button>
            {inputRequiredMissing && <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 8, textAlign: 'center' }}>Add the {typeCfg.inputLabel.toLowerCase()} to continue.</div>}
          </div>

          {caption && (
            <div className="card">
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 10, gap: 8 }}>
                <div className="card-title" style={{ marginBottom: 0 }}>Caption</div>
                <button className="btn btn-sm btn-primary" style={{ fontSize: 11, padding: '4px 12px' }} onClick={copyCaption}>
                  {copied ? '✓ Copied' : 'Copy'}
                </button>
              </div>
              <div style={{ fontSize: 13, color: 'var(--text-primary)', lineHeight: 1.7, whiteSpace: 'pre-wrap', background: 'var(--bg-primary)', border: '1px solid var(--border)', borderRadius: 8, padding: '16px 18px' }}>
                {caption}
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}
